/**
 * Style-related utilities for export functionality
 *
 * Centralised utilities for handling CSS escaping and colour sanitisation
 * used by html2canvas-based export operations.
 *
 * These utilities are shared between:
 * - src/hooks/useArtefactActions.ts (single artefact exports)
 * - src/utils/artefactExport/htmlToImage.ts (batch exports)
 */

/**
 * Regex patterns for unsupported CSS colour functions in html2canvas
 * These modern colour spaces (oklab, oklch, lab, lch, color()) are not supported
 */
export const UNSUPPORTED_COLOR_REGEX = /\b(oklab|oklch|lab|lch|color)\s*\([^)]+\)/gi;

/**
 * Safely escape a string for use in CSS selectors
 * Provides a polyfill for CSS.escape() which may not be available in all environments
 *
 * @param value - The string to escape for use in CSS selectors
 * @returns The escaped string safe for use in querySelector
 */
export function escapeCssSelector(value: string): string {
  const cssGlobal = typeof window !== "undefined" ? (window as any).CSS : undefined;

  if (cssGlobal && typeof cssGlobal.escape === "function") {
    return cssGlobal.escape(value);
  }

  // Polyfill: escape special characters for CSS selectors
  return value.replace(/[!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~]/g, "\\$&");
}

/**
 * Sanitize computed styles that contain unsupported colour functions
 *
 * html2canvas doesn't support modern CSS colour spaces like oklab(), oklch(), etc.
 * This converts them to fallback colours to prevent parsing errors during
 * canvas rendering.
 *
 * @param clonedDoc - The cloned document to sanitize (from html2canvas onclone callback)
 */
export function sanitizeUnsupportedColors(clonedDoc: globalThis.Document): void {
  const allElements = clonedDoc.querySelectorAll("*");

  allElements.forEach((element) => {
    const el = element as HTMLElement;
    const computedStyle = clonedDoc.defaultView?.getComputedStyle(el);

    if (!computedStyle) return;

    // Check common colour properties
    const colorProperties = [
      "color",
      "backgroundColor",
      "borderColor",
      "borderTopColor",
      "borderRightColor",
      "borderBottomColor",
      "borderLeftColor",
      "outlineColor",
      "textDecorationColor",
      "fill",
      "stroke",
    ];

    colorProperties.forEach((prop) => {
      const value = computedStyle.getPropertyValue(prop.replace(/[A-Z]/g, "-$&").toLowerCase());

      if (value && UNSUPPORTED_COLOR_REGEX.test(value)) {
        // Reset regex lastIndex since we're using global flag
        UNSUPPORTED_COLOR_REGEX.lastIndex = 0;
        // Replace unsupported colour with a neutral fallback
        // Use inherit to let parent colour cascade, or transparent for backgrounds
        const fallback = prop.includes("background") ? "transparent" : "inherit";

        el.style.setProperty(prop.replace(/[A-Z]/g, "-$&").toLowerCase(), fallback, "important");
      }
    });
  });
}
